import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { PageHeader } from '@/components/ui/PageHeader/PageHeader';
import { PageContainer } from '@/components/ui/PageContainer';
import { Input } from '@/components/ui/Input/Input';
import { Textarea } from '@/components/ui/Textarea/Textarea';
import { Button } from '@/components/ui/Button/Button';
import { Card } from '@/components/ui/Card/Card';
import { adminFeedbackService, type FeedbackSettings as SettingsType } from '@/services/adminFeedbackService';
import { useNotificationStore } from '@/stores/notificationStore';

export function FeedbackSettings() {
  const notify = useNotificationStore();
  const [isLoading, setIsLoading] = useState(true);
  const [emails, setEmails] = useState<string[]>([]);
  const [newEmail, setNewEmail] = useState('');
  const [aiPrompt, setAiPrompt] = useState('');
  const [isSavingEmails, setIsSavingEmails] = useState(false);
  const [isSavingPrompt, setIsSavingPrompt] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const settings = await adminFeedbackService.getSettings();
      setEmails(settings.notification_emails || []);
      setAiPrompt(settings.ai_system_prompt || '');
    } catch (err) {
      console.error('Failed to load settings:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddEmail = () => {
    const email = newEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) return;
    if (emails.includes(email)) {
      notify.warning('Email already in the list');
      return;
    }
    setEmails([...emails, email]);
    setNewEmail('');
  };

  const handleRemoveEmail = (index: number) => {
    setEmails(emails.filter((_, i) => i !== index));
  };

  const handleSaveEmails = async () => {
    setIsSavingEmails(true);
    try {
      await adminFeedbackService.updateSetting('notification_emails', emails);
      notify.success('Notification emails saved');
    } catch (err: any) {
      notify.error(err.message || 'Failed to save');
    } finally {
      setIsSavingEmails(false);
    }
  };

  const handleSavePrompt = async () => {
    setIsSavingPrompt(true);
    try {
      await adminFeedbackService.updateSetting('ai_system_prompt', aiPrompt);
      notify.success('AI system prompt saved');
    } catch (err: any) {
      notify.error(err.message || 'Failed to save');
    } finally {
      setIsSavingPrompt(false);
    }
  };

  if (isLoading) return <PageContainer className="text-center"><span className="text-muted-foreground">Loading...</span></PageContainer>;

  return (
    <PageContainer>
      <PageHeader
        title="Support Settings"
        description="Configure notification emails and AI behavior for the feedback system."
      />

      {/* Notification Emails */}
      <Card variant="default" padding="md" className="mb-6">
        <h2 className="text-sm font-semibold text-foreground mb-1">Notification Emails</h2>
        <p className="text-xs text-muted-foreground mb-4">
          These email addresses will receive notifications when new tickets are created or AI escalates to human support.
        </p>

        <div className="space-y-2 mb-3">
          {emails.map((email, i) => (
            <div key={i} className="flex items-center justify-between bg-muted rounded-md px-3 py-2">
              <span className="text-sm text-foreground">{email}</span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleRemoveEmail(i)}
                className="p-1 h-auto text-muted-foreground hover:text-error-600"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ))}
          {emails.length === 0 && (
            <p className="text-sm text-muted-foreground">No notification emails configured.</p>
          )}
        </div>

        <div className="flex gap-2">
          <div className="flex-1">
            <Input
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddEmail()}
              placeholder="Add email address"
            />
          </div>
          <Button variant="outline" size="sm" onClick={handleAddEmail}>
            Add
          </Button>
        </div>

        <div className="mt-4">
          <Button
            variant="primary"
            size="sm"
            onClick={handleSaveEmails}
            disabled={isSavingEmails}
            isLoading={isSavingEmails}
          >
            Save Notification Emails
          </Button>
        </div>
      </Card>

      {/* AI System Prompt */}
      <Card variant="default" padding="md">
        <h2 className="text-sm font-semibold text-foreground mb-1">AI System Prompt</h2>
        <p className="text-xs text-muted-foreground mb-4">
          This prompt instructs the AI assistant on how to behave when users ask for help. It controls tone, knowledge boundaries, and escalation behavior.
        </p>

        <Textarea
          value={aiPrompt}
          onChange={(e) => setAiPrompt(e.target.value)}
          placeholder="Enter the system prompt for the AI help assistant..."
          rows={12}
        />

        <div className="mt-4">
          <Button
            variant="primary"
            size="sm"
            onClick={handleSavePrompt}
            disabled={isSavingPrompt}
            isLoading={isSavingPrompt}
          >
            Save AI Prompt
          </Button>
        </div>
      </Card>
    </PageContainer>
  );
}
